import { apiClient } from './api';

export interface UploadUrlResponse {
  uploadUrl: string;
  photoUrl: string;
  thumbnailUrl: string;
  optimizedUrl: string;
  key: string;
  expiresIn: number;
}

export interface PhotoConfirmRequest {
  key: string;
  photoUrl: string;
  thumbnailUrl?: string;
  optimizedUrl?: string;
  /** Position within the apartment gallery. Lower = earlier. Defaults to 0 if omitted. */
  displayOrder?: number;
}

export interface GenerateUploadUrlRequest {
  fileName: string;
  contentType: string;
}

export const photoService = {
  /**
   * Step 1: Get pre-signed upload URL from booking-service
   */
  getUploadUrl: async (
    apartmentId: string,
    fileName: string,
    contentType: string
  ): Promise<UploadUrlResponse> => {
    const requestBody: GenerateUploadUrlRequest = { fileName, contentType };
    const response = await apiClient.post<UploadUrlResponse>(
      `/apartments/${apartmentId}/photos/upload-url`,
      requestBody
    );
    return response.data;
  },

  /**
   * Step 2: Upload file directly to S3 using pre-signed URL
   */
  uploadToS3: async (
    uploadUrl: string,
    file: File,
    onProgress?: (progress: number) => void
  ): Promise<void> => {
    return new Promise((resolve, reject) => {
      const xhr = new XMLHttpRequest();

      // Track upload progress
      if (onProgress) {
        xhr.upload.addEventListener('progress', (e) => {
          if (e.lengthComputable) {
            const percentage = Math.round((e.loaded / e.total) * 100);
            onProgress(percentage);
          }
        });
      }

      xhr.addEventListener('load', () => {
        if (xhr.status === 200) {
          resolve();
        } else {
          reject(new Error(`Upload failed with status ${xhr.status}`));
        }
      });

      xhr.addEventListener('error', () => {
        reject(new Error('Network error during upload'));
      });

      xhr.addEventListener('abort', () => {
        reject(new Error('Upload aborted'));
      });

      xhr.open('PUT', uploadUrl);
      xhr.setRequestHeader('Content-Type', file.type);
      xhr.send(file);
    });
  },

  /**
   * Step 3: Confirm photo upload to booking-service
   */
  confirmPhoto: async (
    apartmentId: string,
    photoData: PhotoConfirmRequest
  ): Promise<void> => {
    await apiClient.post(
      `/apartments/${apartmentId}/photos/confirm`,
      photoData
    );
  },

  /**
   * Delete a photo from apartment
   */
  deletePhoto: async (apartmentId: string, photoKey: string): Promise<void> => {
    await apiClient.delete(
      `/apartments/${apartmentId}/photos/${encodeURIComponent(photoKey)}`
    );
  },
};

